<template>
  <v-container>
    <AppFilterNavigation
      ref="filters"
      :filters.sync="filters"
      filter-name="clubFilters"
    >
      <template #default="prop">
        <v-col cols="12" md="2" class="px-1 mb-2 text-center">
          <AppSelectRegions
            v-model="filters.regionIds"
            :menu-props="customMenuOptions"
            :label="
              $t(
                'calendar:filters.Regions.label',
                'Regions',
                'Label of the regions filter'
              )
            "
            data-test="region"
            :is-filter="!prop.mobile"
            :dense="prop.mobile"
            clearable
            multiple
            include-disabled
          />
        </v-col>

        <v-col cols="12" md="2" class="px-1 mb-2 text-center">
          <AppGenericSelect
            v-model="filters.membershipStatus"
            :items="[
              {
                value: 'active',
                text: $t(
                  'organisations:filters.membership.active.label',
                  'Active',
                  'Label of the organisation membership filter for active organisations'
                )
              },
              {
                value: 'inactive',
                text: $t(
                  'organisations:filters.membership.inactive.label',
                  'Inactive',
                  'Label of the organisation membership filter for inactive organisations'
                )
              }
            ]"
            :menu-props="customMenuOptions"
            data-test="membership"
            :label="
              $t(
                'organisations:filters.membership.label',
                'Membership',
                'Label of the organisation membership filter'
              )
            "
            box-outlined
            :is-filter="!prop.mobile"
            :dense="prop.mobile"
            clearable
            hide-details
          />
        </v-col>

        <v-col cols="12" md="2" class="px-1 mb-2 text-center">
          <AppGenericSelect
            v-model="filters.membershipType"
            :items="[
              {
                value: 'club',
                text: $t(
                  'organisations:filters.type.club.label',
                  'Club',
                  'Label of the organisation type filter for clubs'
                )
              },
              {
                value: 'company',
                text: $t(
                  'organisations:filters.company.club.label',
                  'Company',
                  'Label of the organisation type filter for companies'
                )
              }
            ]"
            data-test="type"
            box-outlined
            :menu-props="customMenuOptions"
            :label="
              $t(
                'organisations:filters.type.label',
                'Type',
                'Label of the organisation type filter'
              )
            "
            :is-filter="!prop.mobile"
            :dense="prop.mobile"
            clearable
            hide-details
          />
        </v-col>

        <v-col cols="12" md="2" class="px-1 mb-2 text-center">
          <AppGenericSelect
            v-model="filters.vatObligation"
            :items="[
              {
                value: true,
                text: $t(
                  'organisations:filters.VAT.yes.label',
                  'Yes',
                  'Label of the organisation VAT filter for yes value'
                )
              },
              {
                value: false,
                text: $t(
                  'organisations:filters.VAT.no.label',
                  'No',
                  'Label of the organisation VAT filter for no value'
                )
              }
            ]"
            data-test="vat"
            :menu-props="customMenuOptions"
            :label="
              $t(
                'organisations:filters.VAT.label',
                'VAT',
                'Label of the organisation VAT filter'
              )
            "
            :is-filter="!prop.mobile"
            :dense="prop.mobile"
            box-outlined
            clearable
            hide-details
          />
        </v-col>

        <v-col
          v-if="$vuetify.breakpoint.mdAndUp"
          cols="12"
          md="4"
          class="px-1 mb-2 text-center"
        >
          <AppSearchBox :value.sync="filters.search" clearable />
        </v-col>
      </template>
      <template #advanced="prop">
        <v-col cols="12" md="3">
          <div class="px-1 mb-2 text-center">
            <AppGenericSelect
              v-model="filters.attributes"
              :items="attributes"
              :menu-props="customMenuOptions"
              ignore-checkboxes-view
              item-text="name"
              item-value="value"
              :label="$t('organisations:filters.attributes.label')"
              data-test="attributes"
              clearable
              multiple
              :is-filter="!prop.mobile"
              :dense="prop.mobile"
              hide-no-data
              hide-details
            />
          </div>
        </v-col>
      </template>
      <template #mobile>
        <AppSearchBox
          :value.sync="filters.search"
          class="px-4"
          clearable
          white
        />
      </template>
    </AppFilterNavigation>

    <div class="d-flex my-auto justify-end pa-3">
      <v-btn
        v-if="$auth.canAccessRoute('OrganisationCreationPage')"
        :to="{ name: 'OrganisationCreationPage' }"
        color="edit"
        large
        text
        class="px-4"
      >
        <b>
          <v-icon class="mr-1" color="edit">$edit</v-icon>
          {{ $t("organisations:button.addOrganisation", "Organisation") }}
        </b>
      </v-btn>

      <AppExportFile
        v-if="$auth.canAccessRoute('OrganisationCreationPage')"
        :prepare-function="exportOrganisations"
        use-tooltip
      />
    </div>

    <v-card color="primary" class="fipa--header" tile />

    <SearchTemplateOrganisations
      data-test="organisations"
      :items="organisations"
      :loading="$apollo.queries.organisations.loading"
      :hide-default-header="$vuetify.breakpoint.smAndDown"
    />
    <div class="text-right">
      <AppLoadMoreBtn
        :has-more-results="hasMoreResults"
        :loading="$apollo.queries.organisations.loading"
        @loadMore="loadMore"
      />
    </div>
  </v-container>
</template>

<script>
import gql from "fraql";
import AppSelectRegions from "@AppSelects/AppSelectRegions.vue";
import SearchTemplateOrganisations from "@/components/table_templates/Organisations.vue";
import AppFilterNavigation from "@elements/AppFilterNavigation.vue";
import AppSearchBox from "@elements/AppSearchBox.vue";
import AppGenericSelect from "@AppSelects/AppGenericSelect.vue";
import AppLoadMoreBtn from "@elements/AppLoadMoreBtn.vue";
import AppExportFile from "@elements/AppExportFile.vue";

const FILTERS = `
  $offset: Int, 
  $limit: Int, 
  $keyword: String,
  $organisationIds: [ID],
  $sortBy: String,
  $sortDesc: Boolean,
  $regionIds: [ID],
  $membershipStatuses: [String],
  $membershipTypes: [String],
  $hasVatObligation: Boolean
  $isRidingSchool: Boolean
`;
const FILTER_VALUES = `
  offset: $offset
  limit: $limit
  keyword: $keyword
  organisationIds: $organisationIds
  sortBy: $sortBy
  sortDesc: $sortDesc
  regionIds: $regionIds
  membershipStatuses: $membershipStatuses
  membershipTypes: $membershipTypes
  hasVatObligation: $hasVatObligation 
  isRidingSchool: $isRidingSchool
`;

export default {
  name: "OrganisationsSearchPage",
  components: {
    SearchTemplateOrganisations,
    AppSelectRegions,
    AppFilterNavigation,
    AppSearchBox,
    AppGenericSelect,
    AppLoadMoreBtn,
    AppExportFile
  },
  data() {
    return {
      inactiveComponent: false,
      organisations: [],
      customMenuOptions: {
        closeOnContentClick: true
      },
      hasMoreResults: true,
      sortBy: "name",
      sortDesc: false,
      filtersCheck: true,
      filters: {
        regionIds: [],
        membershipType: null,
        membershipStatus: "active",
        vatObligation: null,
        search: "",
        attributes: []
      }
    };
  },
  computed: {
    filterVariables() {
      let isRidingSchool = null;
      if (this.filters.attributes.some(x => x.includes("school"))) {
        isRidingSchool = this.filters.attributes
          .find(x => x.includes("school"))
          .split("-")[1];
        isRidingSchool =
          isRidingSchool == "yes"
            ? true
            : isRidingSchool == "no"
            ? false
            : null;
      }
      return {
        offset: 0,
        limit: 50,
        sortBy: this.sortBy,
        sortDesc: this.sortDesc,
        keyword: this.searchString,
        regionIds: this.filters.regionIds,
        membershipStatuses: [this.filters.membershipStatus],
        membershipTypes: [this.filters.membershipType],
        hasVatObligation: this.filters.vatObligation,
        isRidingSchool
      };
    },
    fields() {
      return [
        {
          value: "name",
          text: this.$t(
            "organisations:fields.name.label",
            "Name",
            "Name field label"
          )
        },
        {
          value: "region",
          text: this.$t(
            "organisations:fields.region.label",
            "Region",
            "Region field label"
          ),
          hideMobile: true
        },
        {
          value: "membershipStatus",
          text: this.$t(
            "organisations:fields.membership.label",
            "Membership",
            "Membership field label"
          ),
          hideMobile: true
        },
        {
          value: "membershipType",
          text: this.$t(
            "organisations:fields.membershiptype.label",
            "Membership type",
            "Membership type field label"
          ),
          hideMobile: true
        }
      ];
    },
    searchString() {
      return this.filters.search && this.filters.search.length > 2
        ? this.filters.search
        : null;
    },
    attributes() {
      return [
        {
          header: this.$t(
            "organisations:filters.attributes.ridingSchool.heading"
          )
        },
        {
          name: this.$t("organisations:filters.attributes.ridingSchools.label"),
          value: "school-yes"
        },
        {
          name: this.$t(
            "organisations:filters.attributes.excludeRidingSchoolslabel"
          ),
          value: "school-no"
        }
      ];
    }
  },
  mounted() {
    this.$refs.filters.findURLParams();
    // Find if any params in URL and then send query
    // Prevents sending double queries, one and without variables
    this.filtersCheck = false;
  },
  activated() {
    this.inactiveComponent = false;
  },
  deactivated() {
    this.inactiveComponent = true;
  },
  apollo: {
    organisations: {
      query: gql`
         query organisations (${FILTERS}) {
           organisations (${FILTER_VALUES}) {
            ${SearchTemplateOrganisations.fragments.organisations}
           }
         }
        `,
      variables() {
        return this.filterVariables;
      },
      skip() {
        return this.filtersCheck || this.inactiveComponent;
      },
      result({ loading, data }) {
        if (loading || !data) return;
        this.hasMoreResults = data.organisations.length >= 50;
      },
      fetchPolicy: "cache-and-network"
    }
  },
  methods: {
    loadMore() {
      // Fetch more data and transform the original result
      this.$apollo.queries.organisations
        .fetchMore({
          // New variables
          variables: {
            offset: this.organisations.length
          }
        })
        .then(({ data: { organisations } }) => {
          this.hasMoreResults = organisations.length >= 50;
        });
    },
    async exportOrganisations() {
      const keyMap = [
        {
          column: this.$t(
            "organisations:CsvKeys.ID.label",
            "ID",
            "Csv ID column label"
          ),
          key: "id"
        },
        {
          column: this.$t(
            "organisations:CsvKeys.name.label",
            "Name",
            "Csv name column label"
          ),
          key: "name"
        },
        {
          column: this.$t(
            "organisations:CsvKeys.shortname.label",
            "Short name",
            "Csv short name column label"
          ),
          key: "shortName"
        },
        {
          column: this.$t(
            "organisations:CsvKeys.region.label",
            "Region",
            "Csv region column label"
          ),
          key: "region.name"
        },
        {
          column: this.$t(
            "organisations:CsvKeys.address.label",
            "Address",
            "Csv address column label"
          ),
          key: "address.addressText"
        },
        {
          column: this.$t(
            "organisations:CsvKeys.city.label",
            "City",
            "Csv City column label"
          ),
          key: "address.addressCity"
        },
        {
          column: this.$t(
            "organisations:CsvKeys.index.label",
            "Index",
            "Csv index column label"
          ),
          key: "address.addressIndex"
        },
        {
          column: this.$t(
            "organisations:CsvKeys.country.label",
            "Country",
            "Csv country column label"
          ),
          key: "address.addressCountry.ioc"
        },
        {
          column: this.$t(
            "organisations:CsvKeys.email.label",
            "Email",
            "Csv email column label"
          ),
          key: "email"
        },
        {
          column: this.$t(
            "organisations:CsvKeys.phone.label",
            "Phone",
            "Csv phone column label"
          ),
          key: "phone"
        },
        {
          column: this.$t(
            "organisations:CsvKeys.administrator.label",
            "Administrator",
            "Csv administrator column label"
          ),
          key: "administratorPerson.name"
        },
        {
          column: this.$t(
            "organisations:CsvKeys.regnumber.label",
            "Registration number",
            "Csv reg number column label"
          ),
          key: "businessRegCode"
        },
        {
          column: this.$t(
            "organisations:CsvKeys.VAT.label",
            "VAT obligation",
            "Csv VAT column label"
          ),
          key: "hasVatObligation",
          useGivenMethod: org => {
            if (org.hasVatObligation) {
              return this.$t(
                "organisations:filters.VAT.yes.label",
                "Yes",
                "Label of the organisation VAT filter for yes value"
              );
            } else {
              return this.$t(
                "organisations:filters.VAT.no.label",
                "No",
                "Label of the organisation VAT filter for no value"
              );
            }
          }
        },
        {
          column: this.$t(
            "organisations:CsvKeys.IBAN.label",
            "IBAN",
            "Csv IBAN column label"
          ),
          key: "iban"
        },
        {
          column: this.$t(
            "organisations:CsvKeys.SENSEID.label",
            "SENSE ID",
            "Csv SENSE ID column label"
          ),
          key: "identifier"
        },
        {
          column: this.$t(
            "organisations:CsvKeys.status.label",
            "Status",
            "Csv status column label"
          ),
          key: "membershipStatus"
        },
        {
          column: this.$t(
            "organisations:CsvKeys.type.label",
            "Membership type",
            "Csv membership type column label"
          ),
          key: "membershipType"
        }
      ];

      const { data } = await this.$apollo.query({
        query: gql`
          query organisationsExport(
            ${FILTERS}
          ) {
            organisations(
             ${FILTER_VALUES}
            ) {
              id
              name
              shortName
              regionId
              region @client {
                id
                name
              }
              address {
                id
                addressText
                addressCity
                addressIndex
                addressCountryId
                addressCountry @client {
                  id
                  ioc
                }
              }
              email
              phone
              administratorPerson {
                id
                name
              }
              businessRegCode
              hasVatObligation
              iban
              identifier
              membershipStatus
              membershipType
            }
          }
        `,
        variables: {
          ...this.filterVariables,
          offset: 0,
          limit: 2000
        }
      });

      this.$apollo.queries.organisations.refetch({
        offset: 0
      });

      return {
        keyMap,
        items: data.organisations,
        filename: "organisations-exports"
      };
    }
  }
};
</script>
